import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Checkbox, Button, Input, Popconfirm, Modal, Radio, Form } from 'antd';

import { ELEMENT_PRODUCT_TYPES, ELEMENT_TYPES } from '@/features/constructorCommon/constants';
import { ELEMENT_PRE_FILLED } from '@/features/constructorCommon/constantsPreFilled';
import { AnswerList } from '@/features/constructorCommon/components/AnswerList';
import { QuestionnaireAnswer, QuestionnaireDependedAnswers } from '@/features/constructor';
import { ProductAnswer, ProductDependedAnswers } from '@/features/constructorProducts';
import {
  ActionBox,
  Space,
  Label,
  Divider,
  NestedBox,
  Box,
  AnswerItem
} from '@/features/constructorCommon/styled';
import { OPERATORS } from '@/utils/static';
import { SumConditions } from '@/features/constructor/components/SumConditions';
import { areArraysEqual } from '@/utils/helpers';
import { ConstructorForm } from '@/components/admin/molecules';

const ELEMENT_DATE_FOR_SELECT = [
  {
    label: '-',
    value: null
  },
  {
    label: "no later than today's date",
    value: 'OLD'
  },
  {
    label: "not earlier than today's date",
    value: 'NEW'
  }
];

const ELEMENT_IS_TITLE_FOR_SELECT = [
  {
    label: '-',
    value: null
  },
  {
    label: 'First Name(for person)',
    value: 'FirstName',
    title: 'person'
  },
  {
    label: 'Last Name(for person)',
    value: 'LastName',
    title: 'person'
  },
  {
    label: 'Legal Name(for company)',
    value: 'LegalName',
    title: 'company'
  }
];

export const Question = ({
  questionProp,
  groupId,
  onCreated,
  handleCreate,
  handleUpdate,
  handleDelete,
  isCreating,
  isUpdating,
  isDeleting,
  dataSortedSections,
  dataSortedSumSections,
  loadingSections,
  dependentAnswers,
  setDependentAnswers,
  type,
  ...props
}) => {
  const [question, setQuestion] = useState(questionProp || {});
  const { t } = useTranslation();
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [isDepends, setIsDepends] = useState(false);
  const [isModalDependAnswersOpened, setIsModalDependAnswersOpened] = useState(false);
  const [isModalSumConditionsOpened, setIsModalSumConditionsOpened] = useState(false);
  const [dAnswers, setDAnswers] = useState([]);
  const [questionsSumConditions, setQuestionsSumConditions] = useState([]);
  const [isTitle, setIsTitle] = useState(false);
  const [iban, setIban] = useState(false);

  useEffect(() => {
    if (!questionProp?.id || isModalDependAnswersOpened) return;

    setQuestion(questionProp);
    if (questionProp?.person || questionProp?.company) setIsTitle(true);
    if (questionProp?.sumConditions) setQuestionsSumConditions(questionProp.sumConditions);
    if (questionProp?.visibleConditions && questionProp?.visibleConditions.length > 0)
      setIsDepends(true);
    if (
      questionProp?.minCountSymbols &&
      questionProp?.maxCountSymbols &&
      questionProp?.countFirstLetterSymbolsCountry
    )
      setIban(true);
  }, [questionProp]);

  useEffect(() => {
    if (!dataSortedSections || !dependentAnswers || !dependentAnswers.length) return;

    setDAnswers(dependentAnswers);
  }, [dataSortedSections, dependentAnswers]);

  const listTypes = useMemo(() => {
    const list = [];

    if (type === 'questionnaire') {
      for (const prop in ELEMENT_TYPES) {
        list.push(ELEMENT_TYPES[prop]);
      }
    }
    if (type === 'products') {
      for (const prop in ELEMENT_PRODUCT_TYPES) {
        list.push(ELEMENT_PRODUCT_TYPES[prop]);
      }
    }

    return list;
  }, [ELEMENT_TYPES, ELEMENT_PRODUCT_TYPES]);

  const listPreFilled = useMemo(() => {
    const list = [];

    for (const prop in ELEMENT_PRE_FILLED) {
      list.push(ELEMENT_PRE_FILLED[prop]);
    }

    return list;
  }, [ELEMENT_PRE_FILLED]);

  const listDateForSelect = useMemo(() => {
    const list = [];

    for (const prop in ELEMENT_DATE_FOR_SELECT) {
      list.push(ELEMENT_DATE_FOR_SELECT[prop]);
    }

    return list;
  }, [ELEMENT_DATE_FOR_SELECT]);

  const listIsTitleForSelect = useMemo(() => {
    const list = [];

    for (const prop in ELEMENT_IS_TITLE_FOR_SELECT) {
      list.push(ELEMENT_IS_TITLE_FOR_SELECT[prop]);
    }

    return list;
  }, [ELEMENT_IS_TITLE_FOR_SELECT]);

  const handleAction = useCallback(() => {
    const dAnswersProp = isDepends
      ? dAnswers.length > 0
        ? dAnswers
        : !!questionProp?.visibleConditions?.length
          ? null
          : []
      : [];

    if (question.id) {
      handleUpdate({
        question: {
          ...question,
          isRequired: question.type === 'SUM' ? false : question.isRequired,
          sumConditions: question.type === 'SUM' ? questionsSumConditions : []
        },
        dAnswersProp
      });
    } else {
      handleCreate({
        question: {
          ...question,
          isRequired: question.type === 'SUM' ? false : question.isRequired,
          sumConditions: question.type === 'SUM' ? questionsSumConditions : []
        },
        groupId,
        dAnswersProp
      }).then(() => {
        setQuestion({});
        onCreated && onCreated();
      });
    }
  }, [question, dAnswers, isDepends, questionsSumConditions]);

  const onDelete = () => {
    handleDelete(question);
  };

  const onAddAnswers = (ans) => {
    setDAnswers(ans);

    setIsModalDependAnswersOpened(false);
  };

  const onAddSumConditions = () => {
    setQuestionsSumConditions(questionsSumConditions);

    setIsModalSumConditionsOpened(false);
  };

  const changeIsTitle = (checked) => {
    if (checked) setIsTitle(true);
    if (!checked) {
      setQuestion({ ...question, person: null, company: null });
      setIsTitle(false);
    }
  };

  const setOnChangeIsTitle = (value, item) => {
    if (!value) {
      setQuestion({ ...question, person: null, company: null });
    } else if (item.title === 'person') {
      setQuestion({ ...question, person: value, company: null });
    } else if (item.title === 'company') {
      setQuestion({ ...question, company: value, person: null });
    }
  };

  const onChangeOperator = ({ target: { value } }) => {
    setQuestion({ ...question, visibleOperator: value });
  };

  const onChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setQuestion({ ...question, [name]: checked });
  };

  const canHaveAnswer =
    question.id &&
    (question.type === ELEMENT_TYPES.radio.value ||
      question.type === ELEMENT_TYPES.checkbox.value ||
      question.type === ELEMENT_TYPES.text.value ||
      question.type === 'IBAN' ||
      question.type === 'SWIFT');

  const isDisabled =
    !question.question ||
    !question.type ||
    (question.question === questionProp?.question &&
      question.question_RU === questionProp?.question_RU &&
      question.question_LV === questionProp?.question_LV &&
      question.tooltip === questionProp?.tooltip &&
      question.tooltip_RU === questionProp?.tooltip_RU &&
      question.tooltip_LV === questionProp?.tooltip_LV &&
      question.type === questionProp?.type &&
      question.addToPdf === questionProp?.addToPdf &&
      dAnswers.length === 0 &&
      !!questionProp?.visibleConditions?.length === isDepends &&
      areArraysEqual(questionsSumConditions, questionProp?.sumConditions) &&
      question.sortOrder === questionProp?.sortOrder &&
      question.isRequired === questionProp?.isRequired &&
      question.preFilledValue === questionProp?.preFilledValue &&
      (question.type === 'INPUT'
        ? (!!questionProp?.person || !!questionProp?.company) === isTitle
        : true) &&
      (question.type === 'INPUT'
        ? (!!questionProp?.minCountSymbols &&
            !!questionProp?.maxCountSymbols &&
            !!questionProp?.countFirstLetterSymbolsCountry) === iban
        : true) &&
      question.minCountSymbols === questionProp?.minCountSymbols &&
      question.maxCountSymbols === questionProp?.maxCountSymbols &&
      question.countFirstLetterSymbolsCountry === questionProp?.countFirstLetterSymbolsCountry &&
      question.person === questionProp?.person &&
      question.country === questionProp?.country &&
      question.dateForSelect === questionProp?.dateForSelect &&
      question.onlyLatin === questionProp?.onlyLatin);

  return (
    <Box
      $isNew={!question.id}
      styles={{
        body: {
          padding: '16px'
        }
      }}
    >
      <ConstructorForm>
        <div>
          <Form.Item label={t('Question')}>
            <Input
              name='question'
              value={question.question}
              onChange={(e) => setQuestion({ ...question, question: e.target.value })}
            />
          </Form.Item>
          {type === 'products' && (
            <>
              <Form.Item label={t('Question (RU)')}>
                <Input
                  name='question_RU'
                  value={question.question_RU}
                  onChange={(e) => setQuestion({ ...question, question_RU: e.target.value })}
                />
              </Form.Item>
              <Form.Item label={t('Question (LV)')}>
                <Input
                  name='question_LV'
                  value={question.question_LV}
                  onChange={(e) => setQuestion({ ...question, question_LV: e.target.value })}
                />
              </Form.Item>
            </>
          )}
          <Form.Item label='Type'>
            <Select
              name='type'
              onChange={(value) => setQuestion({ ...question, type: value })}
              value={question.type}
              placeholder={t('Select type')}
              style={{ width: '100%' }}
            >
              {listTypes.map((item, index) => (
                <Select.Option key={`questionType${index}`} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label='Tooltip'>
            <Input
              name='tooltip'
              value={question.tooltip}
              onChange={(e) => setQuestion({ ...question, tooltip: e.target.value })}
            />
          </Form.Item>

          <Form.Item label='Tooltip (RU)'>
            <Input
              name='tooltip_RU'
              value={question.tooltip_RU}
              onChange={(e) => setQuestion({ ...question, tooltip_RU: e.target.value })}
            />
          </Form.Item>

          <Form.Item label='Tooltip (LV)'>
            <Input
              name='tooltip_LV'
              value={question.tooltip_LV}
              onChange={(e) => setQuestion({ ...question, tooltip_LV: e.target.value })}
            />
          </Form.Item>

          {question.id && type === 'questionnaire' && (
            <Form.Item>
              <Checkbox name='addToPdf' checked={question.addToPdf} onChange={onChangeCheckbox}>
                {t('Add to PDF')}
              </Checkbox>
            </Form.Item>
          )}

          <Form.Item>
            <Checkbox
              name='isDepends'
              value={isDepends}
              checked={isDepends}
              onChange={(e) => {
                setIsDepends(e.target.checked);
                if (!e.target.checked) setDAnswers([]);
              }}
            >
              {t('This question depends on the answers to other questions')}
            </Checkbox>
          </Form.Item>

          {isDepends && (
            <>
              <div>
                {dAnswers.map((item, index) => (
                  <AnswerItem key={`dependentAnswer${index}`}>{item.answer}</AnswerItem>
                ))}
              </div>
              <Button onClick={() => setIsModalDependAnswersOpened(true)} type='primary'>
                {question?.visibleConditions?.length
                  ? 'Update dependent Answers'
                  : 'Select dependent Answers'}
              </Button>

              <Space />
              <Radio.Group
                options={OPERATORS}
                onChange={onChangeOperator}
                value={question.visibleOperator}
              />
              <Space />

              <Modal
                title={'Select dependent Answers'}
                width={700}
                style={{
                  top: 20
                }}
                okText={'Add'}
                footer={null}
                open={isModalDependAnswersOpened}
                // onOk={onAddAnswers}
                onCancel={() => setIsModalDependAnswersOpened(false)}
              >
                {type === 'questionnaire' && (
                  <QuestionnaireDependedAnswers
                    dataSortedSections={dataSortedSections}
                    loadingSections={loadingSections}
                    dependentAnswers={dependentAnswers}
                    setDependentAnswers={setDependentAnswers}
                    onAddAnswers={onAddAnswers}
                  />
                )}
                {type === 'products' && (
                  <ProductDependedAnswers
                    dataSortedSections={dataSortedSections}
                    loadingSections={loadingSections}
                    dependentAnswers={dependentAnswers}
                    setDependentAnswers={setDependentAnswers}
                    onAddAnswers={onAddAnswers}
                  />
                )}
              </Modal>
            </>
          )}
          {question.type !== 'SUM' && (
            <Form.Item>
              <Checkbox
                name='isRequired'
                value={question.isRequired}
                checked={question.isRequired}
                onChange={(e) => setQuestion({ ...question, isRequired: e.target.checked })}
              >
                {t('Is required')}
              </Checkbox>
            </Form.Item>
          )}

          {question.id && (
            <Form.Item label={t('Order')}>
              <Input
                name='sortOrder'
                value={question.sortOrder}
                style={{ width: '100px' }}
                onChange={(e) => setQuestion({ ...question, sortOrder: e.target.value })}
              />
            </Form.Item>
          )}

          {(question.type === 'INPUT' ||
            question.type === 'EMAIL' ||
            question.type === 'PHONE') && (
            <>
              <Form.Item label={t('Pre Filled Value')}>
                <Select
                  name='preFilledValue'
                  onChange={(value) => setQuestion({ ...question, preFilledValue: value })}
                  value={question.preFilledValue}
                  placeholder={t('Select Pre Filled Value')}
                  style={{ width: '100%' }}
                >
                  {listPreFilled.map((item, index) => (
                    <Select.Option key={`questionPreFilledValue${index}`} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          {question.type === 'SUM' && type === 'questionnaire' && (
            <>
              <Space />
              <Button onClick={() => setIsModalSumConditionsOpened(true)} type='primary'>
                {questionsSumConditions.length > 0
                  ? t('Update sum conditions')
                  : t('Select sum conditions')}
              </Button>
              <Space />

              <SumConditions
                dataSorted={dataSortedSumSections}
                questionsSumConditions={questionsSumConditions}
                setQuestionsSumConditions={setQuestionsSumConditions}
                onAddConditions={onAddSumConditions}
                isOpen={isModalSumConditionsOpened}
                onClose={() => setIsModalSumConditionsOpened(false)}
              />
            </>
          )}

          {type === 'questionnaire' && question.type !== 'SUM' && (
            <Form.Item>
              <Checkbox
                name='onlyLatin'
                value={question.onlyLatin}
                checked={question.onlyLatin}
                onChange={(e) => setQuestion({ ...question, onlyLatin: e.target.checked })}
              >
                {t('Only Latin')}
              </Checkbox>
            </Form.Item>
          )}
          {question.type === 'INPUT' && (
            <Form.Item>
              <Checkbox
                name='IsTitle'
                value={isTitle}
                checked={isTitle}
                onChange={(e) => changeIsTitle(e.target.checked)}
              >
                {t('Is Title')}
              </Checkbox>
            </Form.Item>
          )}

          {isTitle && (
            <>
              <Form.Item>
                <Select
                  name='isTitle'
                  onChange={(value, title) => setOnChangeIsTitle(value, title)}
                  value={question.person || question.company}
                  placeholder={t('Select Is Title Value')}
                  style={{ width: '100%' }}
                >
                  {listIsTitleForSelect.map((item, index) => (
                    <Select.Option
                      key={`questionIsTitle${index}`}
                      value={item.value}
                      title={item.title}
                    >
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          {question.type === 'INPUT' && (
            <Form.Item>
              <Checkbox
                name='iban'
                value={iban}
                checked={iban}
                onChange={(e) => setIban(e.target.checked)}
              >
                {t('IBAN Question')}
              </Checkbox>
            </Form.Item>
          )}
          {iban && (
            <>
              <Form.Item label={t('Min Count Symbols')}>
                <Input
                  name='minCountSymbols'
                  type='number'
                  min={0}
                  max={100}
                  step={1}
                  value={question.minCountSymbols}
                  style={{ width: '100px' }}
                  onChange={(e) => setQuestion({ ...question, minCountSymbols: e.target.value })}
                />
              </Form.Item>

              <Form.Item label={t('Max Count Symbols')}>
                <Input
                  name='maxCountSymbols'
                  type='number'
                  min={0}
                  max={100}
                  step={1}
                  value={question.maxCountSymbols}
                  style={{ width: '100px' }}
                  onChange={(e) => setQuestion({ ...question, maxCountSymbols: e.target.value })}
                />
              </Form.Item>

              <Form.Item label={t('Number Of First Letters Of Characters')}>
                <Input
                  name='countFirstLetterSymbolsCountry'
                  type='number'
                  min={0}
                  max={100}
                  step={1}
                  value={question.countFirstLetterSymbolsCountry}
                  style={{ width: '100px' }}
                  onChange={(e) =>
                    setQuestion({ ...question, countFirstLetterSymbolsCountry: e.target.value })
                  }
                />
              </Form.Item>
            </>
          )}

          {question.type === 'DATE' && (
            <Form.Item label={t('Date Limit')}>
              <Select
                name='dateForSelect'
                onChange={(value) => setQuestion({ ...question, dateForSelect: value })}
                value={question.dateForSelect}
                placeholder={t('Select Date Limit')}
                style={{ width: '100%' }}
              >
                {listDateForSelect.map((item, index) => (
                  <Select.Option key={`questionDateLimit${index}`} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <ActionBox>
            <Button
              onClick={handleAction}
              disabled={isDisabled}
              type='primary'
              loading={isCreating || isUpdating}
            >
              {question.id ? t('Update Question') : t('Create Question')}
            </Button>
            {question.id && (
              <Popconfirm
                placement='topRight'
                title={t('Are you sure to delete this question?')}
                onConfirm={onDelete}
                okText={t('Yes')}
                cancelText={t('No')}
              >
                <Button danger loading={isDeleting}>
                  {t('Delete Question')}
                </Button>
              </Popconfirm>
            )}
          </ActionBox>
        </div>

        {canHaveAnswer && (
          <NestedBox>
            {question.id && <Divider />}
            {type === 'questionnaire' && !!question.availableAnswers?.length && (
              <>
                <AnswerList list={question.availableAnswers} type={type} {...props} />
              </>
            )}
            {type === 'products' && !!question.productAvailableAnswers?.length && (
              <>
                <AnswerList list={question.productAvailableAnswers} type={type} />
              </>
            )}

            {showCreateNew ? (
              <>
                {type === 'products' && (
                  <ProductAnswer
                    questionId={question.id}
                    onCreated={() => setShowCreateNew(false)}
                  />
                )}
                {type === 'questionnaire' && (
                  <QuestionnaireAnswer
                    questionId={question.id}
                    {...props}
                    onCreated={() => setShowCreateNew(false)}
                  />
                )}
              </>
            ) : (
              <Button onClick={() => setShowCreateNew(true)}>{t('Add new Answer')}</Button>
            )}
          </NestedBox>
        )}
      </ConstructorForm>
    </Box>
  );
};
